import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import styled from 'styled-components';

import { useFeathers } from '../../app/util';

const ELECTION_PAGE_SIZE = 100;

const formatNumber = value => new Intl.NumberFormat().format(value ?? 0);

const electionLabel = election => {
  const name = election.name || election.key;
  return name === election.key ? name : `${name} (${election.key})`;
};

export const SiteFinderUpdaterModal = ({ open, onClose }) => {
  const feathers = useFeathers();
  const [elections, setElections] = useState([]);
  const [selectedElectionKeys, setSelectedElectionKeys] = useState([]);
  const [publishingStatus, setPublishingStatus] = useState('draft');
  const [priorityLevel, setPriorityLevel] = useState('');
  const [updatedAt, setUpdatedAt] = useState('');
  const [loadingElections, setLoadingElections] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [starting, setStarting] = useState(false);
  const [operation, setOperation] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !feathers) return;

    let cancelled = false;
    const loadElections = async () => {
      setElections([]);
      setSelectedElectionKeys([]);
      setPublishingStatus('draft');
      setPriorityLevel('');
      setUpdatedAt('');
      setOperation(null);
      setSubmitted(false);
      setError(null);
      setLoadingElections(true);

      try {
        const allElections = [];
        let skip = 0;
        let hasMore = true;

        while (hasMore) {
          const response = await feathers.getService('elections').find({
            query: {
              status: ['live', 'prep'],
              $select: ['key', 'name', 'date', 'status'],
              $sort: { date: -1 },
              $limit: ELECTION_PAGE_SIZE,
              $skip: skip
            }
          });
          const page = Array.isArray(response) ? response : (response?.data || []);
          allElections.push(...page);
          skip += page.length;
          hasMore = page.length === ELECTION_PAGE_SIZE &&
            (response?.total === undefined || skip < response.total);
        }

        if (!cancelled) {
          setElections(allElections);
          setSelectedElectionKeys(allElections.map(election => election.key));
        }
      } catch (err) {
        console.error('Unable to load elections for site-finder updater:', err);
        if (!cancelled) setError(err?.message || 'Unable to load elections.');
      } finally {
        if (!cancelled) setLoadingElections(false);
      }
    };

    loadElections();
    return () => {
      cancelled = true;
    };
  }, [open, feathers]);

  const invalidatePreview = () => {
    setOperation(null);
    setError(null);
  };

  const toggleElection = electionKey => {
    invalidatePreview();
    setSelectedElectionKeys(current => (
      current.includes(electionKey)
        ? current.filter(key => key !== electionKey)
        : [...current, electionKey]
    ));
  };

  const handleRunQuery = async () => {
    if (selectedElectionKeys.length === 0) return;

    setLoadingPreview(true);
    setOperation(null);
    setError(null);
    try {
      const queryContext = {
        publishingStatus,
        election: selectedElectionKeys,
        ...(priorityLevel ? { priorityLevel } : {}),
        ...(updatedAt ? { updatedAt: new Date(updatedAt).toISOString() } : {})
      };
      const created = await feathers.getService('updater').create({
        operationType: 'site-finder',
        queryContext
      });
      setOperation(created);
    } catch (err) {
      console.error('Unable to preview site-finder updater operation:', err);
      setError(err?.message || 'Unable to count matching candidates.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleConfirm = async () => {
    if (!operation?._id) return;

    setStarting(true);
    setError(null);
    try {
      await feathers.getService('updater').patch(operation._id, {
        $startOperation: true
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Unable to start site-finder updater operation:', err);
      setError(err?.message || 'Unable to start the site-finder operation.');
    } finally {
      setStarting(false);
    }
  };

  const handleClose = () => {
    if (!loadingPreview && !starting) onClose();
  };

  const allSelected = elections.length > 0 &&
    selectedElectionKeys.length === elections.length;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      {submitted ? (
        <>
          <DialogTitle>Run site-finder updater</DialogTitle>
          <DialogContent>
            <SubmittedContent>
              <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
              <Typography variant="h6">Site-finder update started</Typography>
              <Typography variant="body2" color="text.secondary">
                {formatNumber(operation?.targetCount)} candidates will be submitted in background batches.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You will receive an email when the job is complete.
              </Typography>
            </SubmittedContent>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Close</Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>Run site-finder updater</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 2 }}>
              Choose the candidates that should be sent through site-finder. Run the query to preview the impact before confirming.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ marginBottom: 2 }}>
                {error}
              </Alert>
            )}

            <FormGrid>
              <FormControl fullWidth size="small">
                <InputLabel id="site-finder-publishing-status-label">Publishing status</InputLabel>
                <Select
                  labelId="site-finder-publishing-status-label"
                  value={publishingStatus}
                  label="Publishing status"
                  disabled={loadingPreview || starting}
                  onChange={event => {
                    setPublishingStatus(event.target.value);
                    invalidatePreview();
                  }}
                >
                  <MenuItem value="draft">Draft / unpublished</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel id="site-finder-priority-label">Priority</InputLabel>
                <Select
                  labelId="site-finder-priority-label"
                  value={priorityLevel}
                  label="Priority"
                  disabled={loadingPreview || starting}
                  onChange={event => {
                    setPriorityLevel(event.target.value);
                    invalidatePreview();
                  }}
                >
                  <MenuItem value="">Any priority</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Updated on or before"
                type="datetime-local"
                value={updatedAt}
                size="small"
                fullWidth
                disabled={loadingPreview || starting}
                InputLabelProps={{ shrink: true }}
                onChange={event => {
                  setUpdatedAt(event.target.value);
                  invalidatePreview();
                }}
              />
            </FormGrid>

            <ElectionHeader>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Elections
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Live and prep elections are selected by default.
                </Typography>
              </Box>
              {!loadingElections && elections.length > 0 && (
                <Button
                  size="small"
                  disabled={loadingPreview || starting}
                  onClick={() => {
                    invalidatePreview();
                    setSelectedElectionKeys(allSelected ? [] : elections.map(election => election.key));
                  }}
                >
                  {allSelected ? 'Clear all' : 'Select all'}
                </Button>
              )}
            </ElectionHeader>

            <ElectionList>
              {loadingElections ? (
                <LoadingContent>
                  <CircularProgress size={28} />
                  <Typography variant="body2">Loading elections...</Typography>
                </LoadingContent>
              ) : elections.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No live or prep elections were found.
                </Typography>
              ) : (
                elections.map(election => (
                  <ElectionRow key={election.key}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedElectionKeys.includes(election.key)}
                          disabled={loadingPreview || starting}
                          onChange={() => toggleElection(election.key)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">{electionLabel(election)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {election.status}
                          </Typography>
                        </Box>
                      }
                    />
                  </ElectionRow>
                ))
              )}
            </ElectionList>

            {selectedElectionKeys.length === 0 && !loadingElections && elections.length > 0 && (
              <Typography variant="caption" color="error">
                Select at least one election.
              </Typography>
            )}

            {operation && (
              <PreviewResult>
                <Typography variant="overline" color="text.secondary">
                  Matching candidates
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {formatNumber(operation.targetCount)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Confirm to start the site-finder operation for these candidates.
                </Typography>
              </PreviewResult>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={loadingPreview || starting}>
              Cancel
            </Button>
            <Button
              onClick={handleRunQuery}
              disabled={loadingElections || loadingPreview || starting || selectedElectionKeys.length === 0}
            >
              {loadingPreview ? 'Running query...' : operation ? 'Run query again' : 'Run query'}
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={!operation || loadingPreview || starting}
            >
              {starting ? 'Starting...' : 'Confirm and run'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

const FormGrid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(3, minmax(0, 1fr));

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const ElectionHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
`;

const ElectionList = styled.div`
  border: 1px solid #E5E8EF;
  border-radius: 8px;
  margin: 12px 0 4px;
  max-height: 280px;
  overflow-y: auto;
  padding: 4px 12px;
`;

const ElectionRow = styled.div`
  border-bottom: 1px solid #EEF0F4;

  &:last-child {
    border-bottom: 0;
  }
`;

const LoadingContent = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: center;
  min-height: 120px;
`;

const PreviewResult = styled.div`
  background: #F7F9FE;
  border: 1px solid #D6E2FB;
  border-radius: 8px;
  margin-top: 20px;
  padding: 20px;
  text-align: center;
`;

const SubmittedContent = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 32px 0;
  text-align: center;
`;
